import { useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle, Gift01, LayersThree01, TrendUp01 } from "@untitledui/icons";
import type { ContractRatesView, ContractSwitchView } from "@mfa-aff/shared";
import { Callout, ConfirmDialog, JalaliDate, cx, errorMessage, formatNumber, formatPercentBp, notify } from "@mfa-aff/ui";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { Checkbox } from "@mfa-aff/ui/components/base/checkbox/checkbox";
import { useContractSwitch, useContractSwitchConfirm } from "../../api/contract-switch";

const ROWS: { key: keyof ContractRatesView; label: string }[] = [
    { key: "linkBp", label: "فروش جدید" },
    { key: "renewalBp", label: "تمدید" },
];

const pct = (bp: number | null | undefined) => (bp === null || bp === undefined ? "—" : formatPercentBp(bp));

type Target = NonNullable<ContractSwitchView["target"]>;

/** Rows where the level pays less than the contract, now or after an announced change. */
const lowerRows = (contract: ContractRatesView | null, target: Target) =>
    contract
        ? ROWS.filter((r) => {
              const later = target.upcoming?.rates[r.key];
              return target.rates[r.key] < contract[r.key] || (later !== undefined && later < contract[r.key]);
          })
        : [];

/** «قرارداد فعلی شما» next to «سطح X», one row per rate; an announced change shows under the level's rate. */
const Comparison = ({ contract, target }: { contract: ContractRatesView | null; target: Target }) => (
    <div className="overflow-hidden rounded-xl ring-1 ring-secondary ring-inset">
        <table className="w-full text-sm">
            <caption className="sr-only">مقایسهٔ درصدهای شرایط فعلی با سطح {target.levelName}</caption>
            <thead className="bg-secondary">
                <tr>
                    <th scope="col" className="px-3 py-2.5 text-start text-xs font-medium text-tertiary">
                        <span className="sr-only">نوع فروش</span>
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-center text-xs font-semibold text-secondary">
                        شرایط فعلی شما
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-center text-xs font-semibold text-brand-secondary">
                        سطح {target.levelName}
                    </th>
                </tr>
            </thead>
            <tbody className="divide-y divide-secondary">
                {ROWS.map((r) => {
                    const mine = contract?.[r.key];
                    const now = target.rates[r.key];
                    const later = target.upcoming && target.upcoming.rates[r.key] !== now ? target.upcoming.rates[r.key] : null;
                    const lower = mine !== undefined && (now < mine || (later !== null && later < mine));
                    const higher = mine !== undefined && !lower && (now > mine || (later !== null && later > mine));
                    return (
                        <tr key={r.key}>
                            <th scope="row" className="px-3 py-3 text-start font-medium text-secondary">
                                {r.label}
                            </th>
                            <td className="px-3 py-3 text-center font-semibold text-primary digits-tabular">{pct(mine)}</td>
                            <td className="px-3 py-3 text-center">
                                <span className={cx("font-semibold digits-tabular", lower ? "text-warning-primary" : higher ? "text-success-primary" : "text-primary")}>
                                    {pct(now)}
                                </span>
                                {lower && <span className="sr-only"> (کمتر از شرایط فعلی)</span>}
                                {higher && <span className="sr-only"> (بیشتر از شرایط فعلی)</span>}
                                {later !== null && target.upcoming && (
                                    <span className="block text-xs text-tertiary">
                                        از <JalaliDate value={target.upcoming.from} format="dayMonth" />: <span className="font-medium text-secondary digits-tabular">{pct(later)}</span>
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

/** The dialog body: comparison, honest warning, own numbers, next level, gift, what changes, agreement. */
const SwitchDetails = ({ view, target, agree, onAgree, error }: { view: ContractSwitchView; target: Target; agree: boolean; onAgree: (v: boolean) => void; error: string | null }) => {
    const lower = lowerRows(view.contract, target);
    const linkLower = lower.some((r) => r.key === "linkBp");
    const { stats, next } = view;
    return (
        <div className="flex flex-col gap-4">
            <Comparison contract={view.contract} target={target} />
            {target.upcoming && (
                <p className="-mt-2 text-xs leading-5 text-tertiary">
                    درصدهای سطح {target.levelName} از <JalaliDate value={target.upcoming.from} format="long" /> عوض می‌شود؛ عددی که با «از …» در جدول آمده، درصد بعد از آن روز است.
                </p>
            )}

            {lower.length > 0 && view.contract ? (
                <Callout
                    tone="warning"
                    icon={AlertTriangle}
                    title={linkLower ? `در سطح ${target.levelName}، درصد فروش با لینک از شرایط فعلی‌تان کمتر است` : `در سطح ${target.levelName}، بعضی درصدها از شرایط فعلی‌تان کمتر است`}
                >
                    {lower
                        .map((r) => {
                            const later = target.upcoming?.rates[r.key];
                            const value = later !== undefined && later < target.rates[r.key] ? later : target.rates[r.key];
                            return `${r.label} از ${pct(view.contract![r.key])} به ${pct(value)}`;
                        })
                        .join("، ")}{" "}
                    می‌رسد. اگر بیشتر درآمدتان از همین راه‌هاست، پیش از تأیید خوب فکر کنید. با فروش بیشتر و رفتن به سطح‌های بالاتر، این فاصله کم می‌شود.
                </Callout>
            ) : (
                view.contract && (
                    <Callout tone="success" icon={CheckCircle} title={`در سطح ${target.levelName} هیچ درصدی از شرایط فعلی‌تان کمتر نیست`} />
                )
            )}

            <div className="grid grid-cols-2 gap-2.5">
                <div className="flex flex-col gap-1 rounded-xl bg-secondary p-3">
                    <p className="text-xs text-tertiary">فروش تأییدشده در ۹۰ روز</p>
                    <p className="text-lg font-semibold text-primary digits-tabular">{formatNumber(stats.newSales90d)}</p>
                </div>
                <div className="flex flex-col gap-1 rounded-xl bg-secondary p-3">
                    <p className="text-xs text-tertiary">سرویس فعال</p>
                    <p className="text-lg font-semibold text-primary digits-tabular">{formatNumber(stats.activeServices)}</p>
                </div>
            </div>
            <p className="text-sm leading-6 text-tertiary">
                با همین آمار از سطح <span className="font-semibold text-secondary">{target.levelName}</span> شروع می‌کنید.
            </p>

            {next ? (
                <p className="flex gap-2 text-sm leading-6 text-tertiary">
                    <TrendUp01 className="mt-1 size-4 shrink-0 text-fg-success-secondary" aria-hidden="true" />
                    <span>
                        سطح بعد، <span className="font-semibold text-secondary">{next.levelName}</span>: با {formatNumber(next.minActiveServices)} سرویس فعال یا{" "}
                        {formatNumber(next.minNewSales90d)} فروش در ۹۰ روز (هر کدام زودتر). در آن سطح فروش با لینک {pct(next.linkBp)} است.
                    </span>
                </p>
            ) : (
                <p className="text-sm leading-6 text-tertiary">سطح {target.levelName} بالاترین سطح است.</p>
            )}

            {target.gift && (
                <p className="flex gap-2 text-sm leading-6 text-tertiary">
                    <Gift01 className="mt-1 size-4 shrink-0 text-fg-brand-secondary" aria-hidden="true" />
                    <span>
                        هدیهٔ سطح {target.levelName}: <span className="font-medium text-secondary">{target.gift}</span>
                    </span>
                </p>
            )}

            <div className="flex flex-col gap-2 rounded-xl bg-secondary p-4">
                <p className="text-xs font-semibold text-tertiary">پیش از تأیید بدانید:</p>
                <ul className="flex flex-col gap-1.5 text-sm leading-6 text-secondary">
                    {[
                        `از همین حالا پورسانت فروش‌ها و تمدیدهای تازه با درصد سطح ${target.levelName} حساب می‌شود؛ تمدید مشتری‌های قبلی‌تان هم همین‌طور. پورسانت‌هایی که تا امروز ثبت شده عوض نمی‌شود.`,
                        "هر چه بیشتر بفروشید، سطح‌تان بالاتر می‌رود و درصدتان بیشتر می‌شود. اگر فروش کم شود، ممکن است در بازبینی فصلی سطح یک پله پایین بیاید.",
                        "از داخل پنل نمی‌شود به شرایط قبلی برگشت. اگر بعداً خواستید، باید با تیم میزبان‌فا صحبت کنید.",
                    ].map((c) => (
                        <li key={c} className="flex gap-2">
                            <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-fg-warning-secondary" aria-hidden="true" />
                            <span>{c}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="rounded-xl p-3 ring-1 ring-secondary ring-inset">
                <Checkbox label="متوجه شدم و می‌خواهم به سطح‌ها بروم" isSelected={agree} onChange={onAgree} />
            </div>

            {error && (
                <Callout tone="error" icon={AlertCircle}>
                    {error}
                </Callout>
            )}
        </div>
    );
};

/**
 * «تغییر قرارداد و رفتن به سطح‌ها» for a contract partner who may switch: a button and its dialog.
 * Renders nothing for anyone else (the API says `eligible`). `note` is shown above the button.
 */
export const ContractSwitchAction = ({
    enabled,
    note,
    label = "رفتن به سطح‌های عادی",
    color = "primary",
    className,
}: {
    enabled: boolean;
    note?: string;
    label?: string;
    color?: "primary" | "secondary";
    className?: string;
}) => {
    const query = useContractSwitch(enabled);
    const confirm = useContractSwitchConfirm();
    const [open, setOpen] = useState(false);
    const [agree, setAgree] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const view = query.data;
    const target = view?.target;
    if (!enabled || !view?.eligible || !target) return null;

    const onOpenChange = (o: boolean) => {
        setOpen(o);
        if (!o) {
            setAgree(false);
            setError(null);
        }
    };

    return (
        <div className={cx("flex flex-col gap-3", className)}>
            {note && <p className="text-sm leading-6 text-tertiary">{note}</p>}
            <Button color={color} size="md" iconLeading={LayersThree01} onPress={() => setOpen(true)} className="self-start max-sm:w-full">
                {label}
            </Button>
            <ConfirmDialog
                isOpen={open}
                onOpenChange={onOpenChange}
                size="lg"
                tone="warning"
                icon={LayersThree01}
                title="رفتن به سطح‌های عادی"
                description={`به‌جای درصدهای ثابت فعلی، پورسانت شما هم مثل بقیهٔ همکاران با سطح حساب می‌شود. پیش از تأیید، درصدها را کنار هم ببینید.`}
                confirmLabel="بله، به سطح‌ها می‌روم"
                isConfirmDisabled={!agree}
                onConfirm={async () => {
                    setError(null);
                    try {
                        await confirm.mutateAsync();
                        notify.success(`از حالا در سطح ${target.levelName} هستید`, {
                            description: "پورسانت فروش‌ها و تمدیدهای تازه با درصد این سطح حساب می‌شود. با فروش بیشتر، سطح‌تان بالا می‌رود.",
                        });
                    } catch (e) {
                        setError(errorMessage(e));
                        throw e;
                    }
                }}
            >
                <SwitchDetails view={view} target={target} agree={agree} onAgree={setAgree} error={error} />
            </ConfirmDialog>
        </div>
    );
};
